/**
 * HeroPhoneCarousel — phone mockup with an auto-rotating slideshow.
 *
 * Iter 113: light/white phone screen background, dark text. Slide content
 * inverted accordingly. Single shared rotation timer + opacity cross-fade.
 *
 * Each slide is a stylised in-app screen from a different perspective:
 *   • BUSINESS  — clinic-owner KPIs, billing
 *   • CLINICIAN — patient chart, AI scribe
 *   • PATIENT   — booking, HEP exercise
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Calendar, CheckCircle2, Mic, Sparkles, TrendingUp,
  Activity, Heart,
} from 'lucide-react';

// ---- Shared atoms (light theme) -----------------------------------------

const KPIRow = ({ label, value, accent }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-[#003D80]/10 last:border-0">
    <span className="text-[10px] text-[#003D80]/70 uppercase tracking-wider">{label}</span>
    <span className={`text-sm font-bold ${accent || 'text-[#003D80]'}`}>{value}</span>
  </div>
);

const Slide = ({ children, badge, badgeColor = 'bg-[#F2A100] text-[#003D80]', title }) => (
  <div className="h-full w-full flex flex-col bg-white" data-testid="phone-slide">
    {/* iter 113b — pt-9 leaves room below the notch so the title never
        overlaps the device cut-out / status-bar area. */}
    <div className="px-4 pt-9 pb-3">
      <div className="flex items-center justify-between mb-3">
        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${badgeColor}`}>
          {badge}
        </span>
        <span className="text-[9px] text-[#003D80]/60">2:42 PM</span>
      </div>
      <h3 className="text-[#003D80] font-heading font-bold text-sm leading-snug">{title}</h3>
    </div>
    <div className="flex-1 px-4 pb-4 overflow-hidden">{children}</div>
  </div>
);

// ---- BUSINESS slides ----------------------------------------------------

const BusinessKpi = () => (
  <Slide badge="BUSINESS" title="This week at a glance">
    <div className="space-y-2">
      <div className="bg-[#F2EBDB]/60 rounded-lg p-3 border border-[#003D80]/10">
        <div className="text-[10px] text-[#003D80]/60 uppercase tracking-wider">Revenue (7d)</div>
        <div className="flex items-end gap-1">
          <span className="text-2xl font-heading font-bold text-[#003D80]">€8,420</span>
          <span className="text-[#1F8A4F] text-xs font-semibold inline-flex items-center gap-0.5">
            <TrendingUp className="w-3 h-3"/> +12%
          </span>
        </div>
        <div className="flex items-end gap-1 mt-2 h-8">
          {[40, 65, 30, 75, 55, 85, 95].map((h, i) => (
            <div key={i} className={`flex-1 rounded-sm ${i === 6 ? 'bg-[#F2A100]' : 'bg-[#003D80]/30'}`} style={{ height: `${h}%` }}/>
          ))}
        </div>
      </div>
      <div className="bg-[#F2EBDB]/40 rounded-lg p-3 border border-[#003D80]/10">
        <KPIRow label="Appointments" value="142"/>
        <KPIRow label="No-shows" value="3.4%" accent="text-[#1F8A4F]"/>
        <KPIRow label="New patients" value="24"/>
      </div>
    </div>
  </Slide>
);

const BusinessBilling = () => (
  <Slide badge="BUSINESS" title="Subscription & billing">
    <div className="bg-gradient-to-br from-[#F2A100]/20 to-[#F2A100]/5 rounded-lg p-3 border border-[#F2A100]/40 mb-2">
      <div className="text-[10px] text-[#003D80]/60 uppercase tracking-wider">Current plan</div>
      <div className="text-lg font-heading font-bold text-[#003D80]">Growth</div>
      <div className="text-[10px] text-[#003D80]/70 mt-0.5">€69/mo · renews Mar 28</div>
    </div>
    <div className="space-y-1.5">
      <div className="bg-[#F2EBDB]/40 rounded p-2 text-[10px] text-[#003D80]/80">
        <div className="flex justify-between"><span>Seats</span><span className="font-bold">5/5</span></div>
        <div className="h-1 mt-1 bg-[#003D80]/10 rounded-full overflow-hidden">
          <div className="h-full w-full bg-[#F2A100]"/>
        </div>
      </div>
      <div className="bg-[#F2EBDB]/40 rounded p-2 text-[10px] text-[#003D80]/80">
        <div className="flex justify-between"><span>Storage</span><span className="font-bold">28/50 GB</span></div>
        <div className="h-1 mt-1 bg-[#003D80]/10 rounded-full overflow-hidden">
          <div className="h-full w-[56%] bg-[#1F8A4F]"/>
        </div>
      </div>
      <div className="bg-[#F2EBDB]/40 rounded p-2 text-[10px] text-[#003D80]/80">
        <div className="flex justify-between"><span>AI scribe calls</span><span className="font-bold">412/1000</span></div>
        <div className="h-1 mt-1 bg-[#003D80]/10 rounded-full overflow-hidden">
          <div className="h-full w-[41%] bg-[#F2A100]"/>
        </div>
      </div>
    </div>
  </Slide>
);

// ---- CLINICIAN slides ---------------------------------------------------

const ClinicianChart = () => (
  <Slide badge="CLINICIAN" title="Eleanor Vance · Age 42" badgeColor="bg-[#003D80] text-white">
    <div className="bg-[#F2EBDB]/50 rounded-lg p-3 border border-[#003D80]/10 mb-2">
      <div className="text-[9px] text-[#F2A100] font-bold tracking-wider mb-1">SUBJECTIVE</div>
      <div className="text-[10px] text-[#003D80]/85 leading-snug">
        R shoulder pain, lifting overhead.
        Onset 4 weeks. Pain VAS 6/10.
      </div>
    </div>
    <div className="bg-[#F2EBDB]/50 rounded-lg p-3 border border-[#003D80]/10 mb-2">
      <div className="text-[9px] text-[#F2A100] font-bold tracking-wider mb-1.5">RANGE OF MOTION (°)</div>
      <div className="grid grid-cols-4 gap-1 text-center">
        {[
          ['Flex', '90'], ['Abd', '120'],
          ['IR',  '45'], ['ER',   '60'],
        ].map(([k, v], i) => (
          <div key={i} className="bg-white rounded p-1">
            <div className="text-[8px] text-[#003D80]/60">{k}</div>
            <div className="text-xs font-bold text-[#003D80]">{v}°</div>
          </div>
        ))}
      </div>
    </div>
    <button className="w-full bg-[#F2A100] text-[#003D80] rounded-lg py-2 text-[10px] font-bold inline-flex items-center justify-center gap-1.5">
      <Sparkles className="w-3 h-3"/> AI-suggest ICD-10
    </button>
  </Slide>
);

const ClinicianAIScribe = () => (
  <Slide badge="CLINICIAN" title="AI Scribe — live transcript" badgeColor="bg-[#003D80] text-white">
    <div className="flex items-center gap-2 mb-2">
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-[#F2A100]/30 animate-ping"/>
        <div className="relative w-8 h-8 rounded-full bg-[#F2A100] flex items-center justify-center">
          <Mic className="w-4 h-4 text-[#003D80]"/>
        </div>
      </div>
      <div className="flex-1">
        <div className="text-[10px] text-[#003D80]/70 uppercase tracking-wider">Recording</div>
        <div className="text-xs font-bold text-[#003D80]">04:12</div>
      </div>
    </div>
    {/* live waveform */}
    <div className="flex items-end gap-0.5 mb-3 h-8">
      {Array.from({ length: 36 }).map((_, i) => {
        const h = 20 + Math.sin(i * 0.6) * 30 + Math.cos(i * 0.9) * 20;
        return <div key={i} className="flex-1 bg-[#F2A100] rounded-sm" style={{ height: `${Math.max(10, h)}%` }}/>;
      })}
    </div>
    <div className="bg-[#F2EBDB]/50 rounded-lg p-2.5 border border-[#003D80]/10 text-[10px] text-[#003D80]/85 leading-snug">
      &quot;Patient reports lateral knee pain on stairs, no swelling.
      I&apos;ll check Lachman… negative. Palpation tender at IT band insertion.&quot;
    </div>
    <div className="mt-2 text-[9px] text-[#1F8A4F] inline-flex items-center gap-1 font-semibold">
      <CheckCircle2 className="w-3 h-3"/> 4 SOAP fields auto-drafted
    </div>
  </Slide>
);

// ---- PATIENT slides -----------------------------------------------------

const PatientBooking = () => (
  <Slide badge="PATIENT" title="Book your next session" badgeColor="bg-[#003D80] text-white">
    <div className="bg-[#F2EBDB]/50 rounded-lg p-3 border border-[#003D80]/10 mb-2">
      <div className="text-[10px] text-[#003D80]/60 uppercase tracking-wider mb-1.5">Available — Thu Mar 14</div>
      <div className="grid grid-cols-3 gap-1.5">
        {['09:00', '10:30', '11:30', '14:00', '15:30', '17:00'].map((t, i) => (
          <button key={i} className={`py-1.5 rounded text-[10px] font-semibold ${i === 1 ? 'bg-[#F2A100] text-[#003D80]' : 'bg-white border border-[#003D80]/15 text-[#003D80]/85'}`}>
            {t}
          </button>
        ))}
      </div>
    </div>
    <div className="bg-[#F2EBDB]/50 rounded-lg p-2.5 border border-[#003D80]/10 mb-2 flex items-center gap-2">
      <Calendar className="w-4 h-4 text-[#F2A100] shrink-0"/>
      <div className="text-[10px] text-[#003D80]/85 flex-1">
        <div className="font-bold">Knee assessment</div>
        <div className="text-[#003D80]/60">45 min · €85</div>
      </div>
    </div>
    <button className="w-full bg-[#F2A100] text-[#003D80] rounded-lg py-2 text-[11px] font-bold">
      Confirm booking
    </button>
  </Slide>
);

const PatientHEP = () => (
  <Slide badge="PATIENT" title="Today's exercises" badgeColor="bg-[#003D80] text-white">
    {[
      { name: 'Glute bridge',     reps: '3×12', done: true,  icon: Activity },
      { name: 'Single-leg squat', reps: '3×8',  done: true,  icon: Activity },
      { name: 'Hamstring curl',   reps: '3×15', done: false, icon: Activity },
    ].map((ex, i) => (
      <div key={i} className="bg-[#F2EBDB]/50 rounded-lg p-2.5 border border-[#003D80]/10 mb-1.5 flex items-center gap-2.5">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${ex.done ? 'bg-[#1F8A4F]' : 'bg-[#003D80]/10'}`}>
          {ex.done ? <CheckCircle2 className="w-4 h-4 text-white"/> : <ex.icon className="w-3.5 h-3.5 text-[#003D80]/70"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-[#003D80]">{ex.name}</div>
          <div className="text-[10px] text-[#003D80]/60">{ex.reps}</div>
        </div>
        {ex.done && <span className="text-[9px] text-[#1F8A4F] font-bold">DONE</span>}
      </div>
    ))}
    <div className="mt-2 bg-[#F2A100]/15 rounded-lg p-2.5 border border-[#F2A100]/40">
      <div className="text-[10px] text-[#003D80] font-bold flex items-center gap-1">
        <Heart className="w-3 h-3 text-[#F2A100]"/> 2 of 3 complete — keep going!
      </div>
    </div>
  </Slide>
);

const SLIDES = [
  { id: 'business-kpi',     audience: 'business',  Component: BusinessKpi },
  { id: 'business-billing', audience: 'business',  Component: BusinessBilling },
  { id: 'clinician-chart',  audience: 'clinician', Component: ClinicianChart },
  { id: 'clinician-ai',     audience: 'clinician', Component: ClinicianAIScribe },
  { id: 'patient-booking',  audience: 'patient',   Component: PatientBooking },
  { id: 'patient-hep',      audience: 'patient',   Component: PatientHEP },
];

// ---- Phone mockup wrapper -----------------------------------------------

/**
 * HeroPhoneCarousel
 *   `audience`    — optional filter ('business' | 'clinician' | 'patient'). When
 *     omitted, every slide rotates (default landing/marketing slideshow).
 *   `interval`    — ms between auto-advances. Default 3500.
 *   `size`        — 'md' (default, 240×490) or 'sm' (200×410) for smaller stacks.
 *   `startDelay`  — ms to wait before the FIRST auto-advance. Lets us stagger
 *     multiple phones on the same page so they don't tick in lockstep.
 */
export default function HeroPhoneCarousel({ audience, interval = 3500, size = 'md', startDelay = 0 }) {
  const slides = useMemo(
    () => (audience ? SLIDES.filter(s => s.audience === audience) : SLIDES),
    [audience],
  );
  const [idx, setIdx] = useState(0);
  const reduceMotion = useMemo(() => (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ), []);

  useEffect(() => {
    if (reduceMotion || slides.length <= 1) return undefined;
    let timer;
    const startTimer = () => {
      timer = setInterval(() => setIdx(i => (i + 1) % slides.length), interval);
    };
    const initial = setTimeout(startTimer, startDelay);
    return () => {
      clearTimeout(initial);
      if (timer) clearInterval(timer);
    };
  }, [reduceMotion, slides, interval, startDelay]);

  useEffect(() => { if (idx >= slides.length) setIdx(0); }, [slides, idx]);

  const dims = size === 'sm'
    ? { wrap: 'w-[200px] h-[410px]', radius: 'rounded-[34px]', screen: 'rounded-[28px]', border: 'border-[5px]' }
    : { wrap: 'w-[240px] h-[490px]', radius: 'rounded-[40px]', screen: 'rounded-[34px]', border: 'border-[6px]' };

  return (
    <div
      className={`relative ${dims.wrap} ${dims.radius} bg-[#0A1F3D] ${dims.border} border-[#0A1F3D] shadow-[0_25px_60px_rgba(0,0,0,0.45)]`}
      data-testid={`hero-phone-carousel${audience ? `-${audience}` : ''}`}
    >
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-[#0A1F3D] rounded-b-2xl z-10"/>
      <div className={`absolute inset-0 ${dims.screen} overflow-hidden bg-white`}>
        {slides.map((s, i) => (
          <div
            key={s.id}
            data-testid={`phone-slide-${s.id}`}
            className="absolute inset-0 transition-opacity duration-700 ease-out"
            style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? 'auto' : 'none' }}
          >
            <s.Component/>
          </div>
        ))}
      </div>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20" data-testid="phone-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-[#F2A100]' : 'w-1.5 bg-[#003D80]/30'}`}
          />
        ))}
      </div>
    </div>
  );
}
